import { useCallback } from 'react'

import { clsx } from 'clsx'

import {
  BillingPriceInterval,
  type BillingProduct,
} from '@tribeplatform/gql-client/types'

import { BillingIntervalTabs } from './BillingIntervalTabs.js'
import { PlanCard } from './PlanCard.js'
import { PlanCardSkeleton } from './PlanCardSkeleton.js'

const MAX_PLAN_COLUMNS = 3
const DEFAULT_LOADING_PLAN_COUNT = 3

interface Props {
  isLoading: boolean
  plans: BillingProduct[]
  selectedInterval: BillingPriceInterval | null
  onIntervalChange: (interval: BillingPriceInterval) => void
}

export const PlanCardsSection = ({
  isLoading,
  plans,
  selectedInterval,
  onIntervalChange,
}: Props) => {
  const preferredPlanCount =
    plans.length || (isLoading ? DEFAULT_LOADING_PLAN_COUNT : 1)
  const preferredPlanColumns = Math.min(
    Math.max(preferredPlanCount, 1),
    MAX_PLAN_COLUMNS,
  )

  const handleTabChange = useCallback(
    (index: number) => {
      onIntervalChange(
        index === 0 ? BillingPriceInterval.Year : BillingPriceInterval.Month,
      )
    },
    [onIntervalChange],
  )

  return (
    <div className="@container/plan-cards space-y-6">
      {selectedInterval && (
        <BillingIntervalTabs
          selectedInterval={selectedInterval}
          onChange={handleTabChange}
        />
      )}
      <div
        className={clsx('grid grid-cols-1 gap-5', {
          '@2xl/plan-cards:grid-cols-2': preferredPlanColumns >= 2,
          '@4xl/plan-cards:grid-cols-3': preferredPlanColumns >= 3,
        })}
      >
        {isLoading &&
          [...Array(preferredPlanColumns)].map((_e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <PlanCardSkeleton key={i} />
          ))}
        {!isLoading &&
          selectedInterval &&
          plans.map(product => (
            <PlanCard
              key={product.id}
              product={product}
              interval={selectedInterval}
            />
          ))}
      </div>
    </div>
  )
}
